import type {
  GameAssetService,
  GameClock,
  GameContext,
  GameEvent,
  GameAudioVoice,
  GameViewportService,
} from "../contracts/game";
import type { GameScope, GameScopeFactory } from "../app/game-host";
import { Mulberry32 } from "../shared/mulberry32";
import { AssetService } from "./assets/asset-service";
import type { AudioBufferLike } from "./audio/audio-service";
import type { DiagnosticsService, InputTransition } from "./diagnostics/diagnostics-service";
import { bindPointerInput } from "./input/input-binding";
import { InputService } from "./input/input-service";
import { bindTouchFallback } from "./input/touch-fallback";
import { FixedStepClock } from "./timing/fixed-step-clock";

export interface DisposableAudioScope {
  play(effectId: string, buffer: AudioBufferLike, gain?: number): boolean;
  startLoop(effectId: string, buffer: AudioBufferLike, gain?: number): GameAudioVoice | null;
  decode(effectId: string, data: ArrayBuffer): Promise<AudioBufferLike>;
  destroy(): void;
}

export interface AudioManagerLike {
  createScope(): DisposableAudioScope;
}

export interface InputBindingLike {
  destroy(): void;
}

export type InputBindingFactory = (
  host: HTMLElement,
  scope: Window,
  document: Document,
  input: InputService,
) => InputBindingLike;

export interface PlatformServicesOptions {
  scope: Window;
  viewport: GameViewportService;
  diagnostics: DiagnosticsService;
  audioManager: AudioManagerLike;
  bindInput?: InputBindingFactory;
  assetOrigin?: string;
  seedSource?: () => number;
}

export interface CombinedAbortSignal {
  signal: AbortSignal;
  dispose(): void;
}

export function combineAbortSignals(
  scopeSignal: AbortSignal,
  requestSignal?: AbortSignal,
): CombinedAbortSignal {
  if (!requestSignal) {
    return { signal: scopeSignal, dispose: () => undefined };
  }

  const controller = new AbortController();
  const abort = (): void => controller.abort();
  let disposed = false;

  if (scopeSignal.aborted || requestSignal.aborted) {
    controller.abort();
    return { signal: controller.signal, dispose: () => undefined };
  }

  scopeSignal.addEventListener("abort", abort, { once: true });
  requestSignal.addEventListener("abort", abort, { once: true });

  return {
    signal: controller.signal,
    dispose: () => {
      if (disposed) return;
      disposed = true;
      scopeSignal.removeEventListener("abort", abort);
      requestSignal.removeEventListener("abort", abort);
    },
  };
}

function defaultInputBinding(
  host: HTMLElement,
  scope: Window,
  document: Document,
  input: InputService,
): InputBindingLike {
  return "PointerEvent" in scope
    ? bindPointerInput(host, scope, document, input)
    : bindTouchFallback(host, scope, document, input);
}

function monotonicNow(scope: Window): number {
  const performanceScope = scope.performance;
  return performanceScope && typeof performanceScope.now === "function"
    ? performanceScope.now()
    : Date.now();
}

function createDiagnosticClock(diagnostics: DiagnosticsService): GameClock {
  const fixedClock = new FixedStepClock();
  return {
    get droppedTimeMs(): number {
      return fixedClock.droppedTimeMs;
    },
    reset(): void {
      fixedClock.reset();
    },
    consume(timestampMs, step) {
      const before = fixedClock.droppedTimeMs;
      const frame = fixedClock.consume(timestampMs, step);
      const newlyDropped = fixedClock.droppedTimeMs - before;
      if (newlyDropped > 0) diagnostics.addDroppedSimulationTime(newlyDropped);
      return frame;
    },
  };
}

export class PlatformServices implements GameScopeFactory {
  private readonly scope: Window;
  private readonly viewport: GameViewportService;
  private readonly diagnostics: DiagnosticsService;
  private readonly audioManager: AudioManagerLike;
  private readonly bindInput: InputBindingFactory;
  private readonly assetOrigin: string;
  private readonly seedSource: () => number;

  constructor(options: PlatformServicesOptions) {
    this.scope = options.scope;
    this.viewport = options.viewport;
    this.diagnostics = options.diagnostics;
    this.audioManager = options.audioManager;
    this.bindInput = options.bindInput ?? defaultInputBinding;
    this.assetOrigin = options.assetOrigin ?? options.scope.location.origin;
    this.seedSource = options.seedSource ?? (() => Date.now() >>> 0);
  }

  create(
    gameId: string,
    host: HTMLElement,
    emit: (event: GameEvent) => void,
    requestHome: () => void,
  ): GameScope {
    const controller = new AbortController();
    const input = new InputService(() => null, (transition) => {
      const record: InputTransition = {
        at: monotonicNow(this.scope),
        type: transition.type,
        pointerId: transition.pointerId,
        action: transition.action,
      };
      if (transition.point) {
        record.x = transition.point.x;
        record.y = transition.point.y;
      }
      this.diagnostics.recordInput(record);
    });
    const inputBinding = this.bindInput(host, this.scope, this.scope.document, input);
    const audio = this.audioManager.createScope();
    const assetService = new AssetService(this.assetOrigin);
    const assets: GameAssetService = {
      arrayBuffer: async (path, signal) => {
        const combined = combineAbortSignals(controller.signal, signal);
        try {
          return await assetService.arrayBuffer(path, combined.signal);
        } finally {
          combined.dispose();
        }
      },
      json: async (path, validate, signal) => {
        const combined = combineAbortSignals(controller.signal, signal);
        try {
          return await assetService.json(path, validate, combined.signal);
        } finally {
          combined.dispose();
        }
      },
    };
    const clock = createDiagnosticClock(this.diagnostics);
    const random = new Mulberry32(this.seedSource());

    const context: GameContext = {
      gameId,
      signal: controller.signal,
      input,
      audio,
      viewport: this.viewport,
      clock,
      assets,
      diagnostics: this.diagnostics,
      random,
      emit,
      requestHome,
    };

    let disposed = false;
    return {
      context,
      abort: () => {
        if (!controller.signal.aborted) controller.abort();
        input.releaseAll();
      },
      dispose: () => {
        if (disposed) return;
        disposed = true;
        inputBinding.destroy();
        audio.destroy();
        input.destroy();
      },
    };
  }
}
